import { useTranslation } from '@dukanti/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useNavBlockerClaimed, useNavBlockerState } from '@/hooks/use-nav-blocker';

/**
 * Single dialog instance that serves every route inside AdminShellLayout.
 * Reads blocker state from NavBlockerContext via useNavBlockerState() and
 * renders only when blocker.state === 'blocked'.
 *
 * S6 (F-027 §5.2.1) — Escape, outside-click, and the "Stay" button ALL
 * flow through onOpenChange → blocker.reset(). Only the explicit "Leave"
 * button calls blocker.proceed(). If we didn't reset() on every implicit-
 * close path, the dialog would close visually while the blocker stayed in
 * 'blocked' state, and the next sidebar click would re-open it with no
 * explanation.
 *
 * Epic #228 P4 (PD11): a block that a dirty scope claims (`useNavBlocker`'s
 * `onBlocked`) is decided by that scope; this dialog stays closed for it.
 */
export function AdminShellUnsavedDialog() {
  const { t } = useTranslation();
  const blocker = useNavBlockerState();
  const claimed = useNavBlockerClaimed();
  const open = blocker.state === 'blocked' && !claimed;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && blocker.state === 'blocked' && !claimed) {
          blocker.reset?.();
        }
      }}
    >
      <AlertDialogContent data-testid="admin-shell-unsaved-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('admin.nav.unsavedChanges.title')}</AlertDialogTitle>
          <AlertDialogDescription>{t('admin.nav.unsavedChanges.body')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="admin-shell-unsaved-stay">
            {t('admin.nav.unsavedChanges.stay')}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="admin-shell-unsaved-leave"
            onClick={() => blocker.proceed?.()}
          >
            {t('admin.nav.unsavedChanges.leave')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
